import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/tokens.css';
import './styles/themes.css';
import './styles/base.css';

import { App } from './app/App';
import { SafeModeBoundary } from './app/SafeModeBoundary';
import { renderBootFloor } from './app/bootFloor';
import { initServiceWorker } from './app/serviceWorker';
import { claimPersistentStorage } from './data/storagePersist';
import { seedIfEmpty } from './data/seed';
import { useProfileStore } from './core/profile/ProfileStore';
import { setupSpeech } from './core/speech/setup';
import { restoreDraft } from './core/composer/draft';
import { startDiagnostics } from './core/diagnostics/DiagnosticsService';
import { startLearning } from './core/learning/LearningService';
import { startRestoreOffer } from './components/RestoreOffer';
import { reportPrivateOutput } from './core/speech/privateOutput';
import { startBackup } from './core/backup/BackupService';

const root = document.getElementById('root');

if (!root) {
  // Cannot happen with our own index.html, but a missing mount point is the one
  // failure that would otherwise be a white screen with no code path to fix it.
  document.body.innerHTML = '<p style="font:400 32px sans-serif;padding:24px">Sag\'s</p>';
} else {
  try {
    createRoot(root).render(
      <StrictMode>
        <SafeModeBoundary>
          <App />
        </SafeModeBoundary>
      </StrictMode>,
    );
  } catch (err) {
    // React itself did not start. Below the boundary, so hand-built DOM.
    renderBootFloor(root, err);
  }
}

// Everything below is deliberately after render and deliberately non-blocking:
// nothing may sit between the person and a keyboard.

// Storage durability is claimed on every launch, not once at install
// (INV-04). The grant is heuristic and can change.
void claimPersistentStorage();

// Idempotent: does nothing once the device has been used, so it can never
// overwrite a caregiver's edits or resurrect a deleted phrase. Settings are
// hydrated after seeding so a first launch reads the row it just wrote.
void seedIfEmpty()
  .catch((err: unknown) => {
    console.error('[seed] failed', err);
  })
  .then(() => useProfileStore.getState().hydrate());

// Audio session, unlock-on-first-gesture, the provider chain and the warm-up ping.
// Registering providers touches no network and creates no AudioContext until a gesture.
setupSpeech();

// The sentence carried across the one reload the person can ask for (`SHL-09`, ADR-042). Read and cleared
// once: it is a handover, not persisted composer state, and a draft that survived twice would put a stale
// sentence in front of somebody who had moved on.
void restoreDraft();

// Two subscribers to the same event, doing deliberately opposite things: diagnostics keep metadata and
// strip the text (NFR7), learning keeps the words because the words are the point.
startDiagnostics();
startLearning();

// The first-run restore offer's one question, resolved at boot rather than inside a component that may never
// render — the band's owner has to know whether the offer applies before deciding to show it.
startRestoreOffer();

// 3.6: whether key echo could ever be routed to an earbud. Reported, not acted on — there is nothing to do
// differently, and a device that silently assumed the limitation would leave the next person to rediscover it.
reportPrivateOutput();

// Automatic backup (4.6). Deliberately last, deliberately late: the attempt itself waits 20 s after launch,
// skips while speech is in flight, does nothing at all until a caregiver has minted the credentials, and
// fails silently. The seconds after a launch belong to the person starting a sentence.
startBackup();

initServiceWorker();
